import { registerSW } from 'virtual:pwa-register';
import type { Router } from 'vue-router';

/**
 * Registro del service worker y recuperación ante versiones viejas (req. 1.4).
 *
 * Causa del bug "no puedo abrir el modelo desde el QR": la app instalada en el
 * celular queda abierta días con una versión vieja. Al hacer un deploy, el
 * service worker nuevo se activaba por debajo (skipWaiting + clientsClaim) y
 * borraba del caché los archivos de la versión vieja, que tampoco existen ya en
 * el servidor. La pantalla abierta seguía corriendo el código viejo y, al
 * navegar a una ruta (la ficha del QR se carga por demanda), no encontraba su
 * archivo y quedaba en blanco. Solo se arreglaba borrando los datos del sitio.
 *
 * Qué hace este módulo:
 * - Al abrir la app, antes de mostrar nada, compara la versión del código que
 *   corre (__APP_VERSION__, fijada en el build) con la publicada en
 *   /version.json (que nginx sirve sin caché). Si no coinciden, muestra
 *   "Actualizando" y fuerza la actualización: así nadie carga un formulario
 *   con una versión vieja, por ejemplo sin un campo obligatorio nuevo.
 * - Registra el service worker en modo prompt: el worker nuevo queda en espera
 *   y la app decide cuándo activarlo y recargar. La versión vieja conserva sus
 *   archivos en caché mientras tanto, así que nunca queda en blanco.
 * - Aplica la versión nueva enseguida, salvo que el usuario esté en una
 *   pantalla con datos sin guardar (meta.unsavedForm en el router): ahí espera
 *   a que salga de esa pantalla para no perderle lo cargado.
 * - Busca actualizaciones al volver la app a primer plano y cada hora, porque
 *   la app instalada puede no "navegar" nunca y el navegador solo chequea el
 *   service worker en cada navegación.
 * - Si igual falla la carga de un archivo de la app (vite:preloadError), recarga
 *   la página una sola vez para traer la versión vigente.
 */

/** Cada cuánto se busca una versión nueva mientras la app está abierta */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** Marca en sessionStorage (con fecha) para no entrar en un bucle de recargas */
const RELOAD_GUARD_KEY = 'matchup:reloaded-after-preload-error';
/** Si la última recarga por este motivo fue hace menos de esto, no se vuelve a recargar */
const RELOAD_GUARD_TTL_MS = 30 * 1000;

/** Marca para no forzar la actualización en bucle si el servidor publica una versión inconsistente */
const FORCED_UPDATE_GUARD_KEY = 'matchup:forced-update-at';
const FORCED_UPDATE_GUARD_TTL_MS = 60 * 1000;

/** Tiempo máximo esperando /version.json al abrir: sin red, la app arranca igual */
const VERSION_FETCH_TIMEOUT_MS = 4000;
/** Tiempo máximo esperando que el service worker nuevo tome el control antes de limpiar todo */
const FORCED_UPDATE_TIMEOUT_MS = 8000;

let registration: ServiceWorkerRegistration | undefined;
let updateServiceWorker: ((reloadPage?: boolean) => Promise<void>) | undefined;
/** Hay una versión nueva instalada y a la espera de que la app la active */
let updatePending = false;
let router: Router | undefined;

/** Pide al navegador que compare el service worker instalado con el del servidor */
export async function checkForUpdates(): Promise<void> {
  try {
    await registration?.update();
  } catch {
    /* sin red o sin service worker: se reintenta en el próximo chequeo */
  }
}

/**
 * Último recurso para soporte: desregistra el service worker, borra los cachés
 * de la app y recarga. Equivale a "borrar datos del sitio" sin perder la sesión
 * (que vive en localStorage).
 */
export async function hardRefresh(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } finally {
    window.location.reload();
  }
}

/**
 * Recarga la página una sola vez cuando falla la carga de un chunk de la app.
 * Devuelve true si se disparó la recarga.
 */
export function reloadOnceAfterLoadError(targetUrl?: string): boolean {
  if (recentlyMarked(RELOAD_GUARD_KEY, RELOAD_GUARD_TTL_MS)) return false;
  if (targetUrl) window.location.assign(targetUrl);
  else window.location.reload();
  return true;
}

/** Indica si un error de vue-router o de import() es por un chunk que ya no existe */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /Loading (CSS )?chunk .* failed/i.test(message)
  );
}

/**
 * Devuelve true si la marca se puso hace menos de ttl; si no, la pone ahora.
 * Sirve para hacer algo una sola vez por ventana de tiempo
 */
function recentlyMarked(key: string, ttlMs: number): boolean {
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0);
    if (Date.now() - last < ttlMs) return true;
    sessionStorage.setItem(key, String(Date.now()));
  } catch {
    /* sessionStorage bloqueado: se actúa igual */
  }
  return false;
}

/** El usuario está en una pantalla donde recargar le perdería datos cargados */
function onUnsavedForm(): boolean {
  return !!router?.currentRoute.value.meta?.unsavedForm;
}

/** Activa el service worker en espera; la página se recarga cuando toma el control */
function applyPendingUpdate(): void {
  if (!updatePending || !updateServiceWorker) return;
  updatePending = false;
  void updateServiceWorker(true);
}

/** Versión publicada en el servidor, o null si no se pudo consultar */
async function fetchPublishedVersion(): Promise<string | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), VERSION_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { version?: unknown };
    return typeof data.version === 'string' ? data.version : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function showUpdatingSplash(): void {
  const root = document.getElementById('app');
  if (!root) return;
  root.innerHTML =
    '<div class="boot-splash"><div class="boot-splash__spinner"></div><p>Actualizando Match-Up a la última versión…</p></div>';
}

/**
 * Se llama antes de montar la app. Si el código que corre no es el publicado,
 * fuerza la actualización y la promesa no se resuelve hasta recargar: la app
 * vieja no llega a mostrarse. Si la versión coincide, no hay red o ya se forzó
 * hace un instante, resuelve enseguida y la app arranca normalmente.
 */
export async function ensureLatestVersion(): Promise<void> {
  if (__APP_VERSION__ === 'dev') return;
  const published = await fetchPublishedVersion();
  if (!published || published === __APP_VERSION__) return;
  // Hace un momento ya se forzó y seguimos viejos: el servidor publica una
  // versión que no coincide con sus archivos. Se arranca igual para no bloquear
  if (recentlyMarked(FORCED_UPDATE_GUARD_KEY, FORCED_UPDATE_GUARD_TTL_MS)) return;

  showUpdatingSplash();
  await checkForUpdates();
  // Si el worker nuevo se instaló, onNeedRefresh ya disparó la recarga. Si
  // no llega, se limpia todo y se recarga desde el servidor
  await new Promise((resolve) => window.setTimeout(resolve, FORCED_UPDATE_TIMEOUT_MS));
  await hardRefresh();
  // La recarga está en curso: se espera para que no se monte la app vieja
  await new Promise(() => {});
}

export function setupPwa(appRouter: Router): void {
  router = appRouter;

  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      updatePending = true;
      if (!onUnsavedForm()) applyPendingUpdate();
    },
    onRegisteredSW(_swUrl, reg) {
      registration = reg;
      if (!reg) return;

      // Chequeo periódico mientras la app queda abierta
      window.setInterval(() => void checkForUpdates(), UPDATE_CHECK_INTERVAL_MS);

      // Chequeo al volver a primer plano (la app instalada vuelve del fondo)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void checkForUpdates();
      });
    },
    onRegisterError(error) {
      console.warn('No se pudo registrar el service worker', error);
    },
  });

  // Si la versión nueva quedó esperando por un formulario abierto, se aplica
  // al salir de esa pantalla
  router.afterEach((to) => {
    if (updatePending && !to.meta?.unsavedForm) applyPendingUpdate();
  });

  // Vite avisa cuando no puede cargar un chunk o su CSS (versión vieja de la app)
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnceAfterLoadError()) event.preventDefault();
  });
}
